'use client';

import { useState, useEffect } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Info, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import { SINGLE_TRANSLATION_LIMITS } from '@/lib/constants';

interface CharacterCounterProps {
  characterCount: number;
  disabled?: boolean;
  className?: string;
}

export function CharacterCounter({ characterCount, disabled = false, className }: CharacterCounterProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const t = useTranslations('characterCounter');

  const maxChars = SINGLE_TRANSLATION_LIMITS.MAX_CHARACTERS;
  const premiumMaxChars = SINGLE_TRANSLATION_LIMITS.PREMIUM_MAX_CHARACTERS;
  const percentage = (characterCount / maxChars) * 100;
  const isOverLimit = percentage > 100;

  // Automatically show popup when limit is exceeded
  useEffect(() => {
    if (isOverLimit && !disabled) {
      setIsPopoverOpen(true);
    }
  }, [isOverLimit, disabled]);

  // Only show if over 50% of the limit
  if (percentage < 50) {
    return null;
  }

  const getVariant = () => {
    if (percentage > 100) return 'destructive';
    if (percentage >= 80) return 'secondary';
    return 'outline';
  };

  const getIcon = () => {
    if (percentage > 100) return <AlertTriangle className="h-3 w-3" />;
    if (percentage >= 80) return <AlertTriangle className="h-3 w-3" />;
    return <Info className="h-3 w-3" />;
  };

  return (
    <div className={className}>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Badge
            variant={getVariant()}
            className={`gap-1 text-xs cursor-pointer transition-colors duration-200 ${disabled
              ? 'opacity-50 cursor-not-allowed'
              : isOverLimit
                ? 'hover:bg-destructive/75'
                : percentage >= 80
                  ? 'hover:bg-muted-foreground/35'
                  : 'hover:bg-muted-foreground/25'
              }`}
          >
            {getIcon()}
            {characterCount.toLocaleString()}/{maxChars.toLocaleString()}
          </Badge>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="font-medium flex items-center gap-2">
                {getIcon()}
                {t('title')}
              </h4>
              <p className={`text-sm ${isOverLimit ? 'text-primary' : 'text-muted-foreground'}`}>
                {isOverLimit
                  ? t('overLimitDescription', { maxChars: maxChars.toLocaleString() })
                  : t('approachingLimitDescription', { maxChars: maxChars.toLocaleString() })
                }
              </p>
            </div>

            <div className="rounded-lg border bg-muted/50 p-3">
              <div className="text-sm font-medium mb-2">{t('currentUsage')}</div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('characters')}</span>
                <Badge variant={getVariant()}>
                  {characterCount.toLocaleString()} / {maxChars.toLocaleString()}
                </Badge>
              </div>
              {isOverLimit && (
                <div className="mt-2 text-xs text-muted-foreground">
                  {t('overBy', { excess: (characterCount - maxChars).toLocaleString() })}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="text-center text-primary">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Sparkles className="h-4 w-4" />
                  <span className="font-medium">{t('premiumSoon')}</span>
                  <Sparkles className="h-4 w-4" />
                </div>
                <div className="text-lg font-bold">
                  {t('premiumLimit', { limit: premiumMaxChars.toLocaleString() })}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {t('premiumDescription')}
                </p>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
